import { ArrowsExpandIcon } from "@heroicons/react/outline";
import Editor from "@monaco-editor/react";
import type { editor } from "monaco-editor";
import { type ReactNode, useRef, useState } from "react";

import { Button } from "../Button";
import { CopyButton } from "../copy-button";
import { cn } from "../utils";

import { Dialog, DialogContent } from "./dialog";
import { LanguageSelector } from "./LanguageSelector";
import RuntimeDetails from "./RuntimeDetails";
import type { LanguageType } from "./types";

export interface CodeEditorProps {
  code: string;
  selectedLanguage: LanguageType;
  editorOptions?: editor.IStandaloneEditorConstructionOptions;
  dataIsValid?: { code: boolean };
  className?: string;
  showRuntimeDetails?: boolean;
  showFullScreenButton?: boolean;
  fullHeight?: boolean;
  height?: string;
  width?: string;
  controlsType?: "tabs" | "dropdown";
  showLanguageSelector?: boolean;
  action?: ReactNode;
  languages: LanguageType[];
  onCopyCode?: (code: string) => void;
  handleCodeChange?: (code: string) => void;
  handleLanguageChange?: (language: LanguageType) => void;
  languageSelectorLabel?: string;
  docsLink?: string;
}

const EDITOR_OPTIONS = {
  minimap: { enabled: false },
  scrollBeyondLastLine: false,
  fontSize: 13,
  lineHeight: 20,
  padding: { top: 12, bottom: 12 },
  renderLineHighlight: "none",
  fontFamily: "menlo, monaco, monospace",
  automaticLayout: true,
} as const;

const MONACO_LANGUAGE_BY_TYPE: Record<LanguageType, string> = {
  PYTHON: "python",
  JAVASCRIPT: "javascript",
  CURL: "shell",
  BASH: "shell",
};

const CodeEditor = ({
  editorOptions,
  dataIsValid,
  className,
  showRuntimeDetails = true,
  height = "300px",
  showFullScreenButton = true,
  fullHeight = false,
  languageSelectorLabel,
  selectedLanguage,
  controlsType = "tabs",
  showLanguageSelector = true,
  action,
  code,
  handleCodeChange,
  handleLanguageChange,
  languages,
  docsLink,
  onCopyCode,
}: CodeEditorProps) => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const modalContainerRef = useRef<HTMLDivElement>(null);

  const monacoLanguage = MONACO_LANGUAGE_BY_TYPE[selectedLanguage];
  const hasLanguageSelector = showLanguageSelector && languages.length > 0;

  return (
    <div
      className={cn(
        "flex flex-col",
        hasLanguageSelector && controlsType === "tabs" ? "" : "gap-2",
        fullHeight ? "h-full" : "",
      )}
    >
      {hasLanguageSelector && (
        <div className="flex h-fit">
          <LanguageSelector
            type={controlsType}
            action={action}
            docsLink={docsLink}
            label={languageSelectorLabel}
            isReadOnly={editorOptions?.readOnly}
            languages={languages}
            selectedLanguage={selectedLanguage}
            setSelectedLanguage={(language) => handleLanguageChange?.(language)}
          />
        </div>
      )}
      <div
        ref={containerRef}
        className={cn(
          "relative col-span-3 w-full overflow-hidden rounded-md border border-gray-200 pt-2",
          className,
          hasLanguageSelector && controlsType === "tabs"
            ? "rounded-tl-none rounded-tr-none border-t-0 pt-0"
            : "",
          fullHeight ? "flex-1" : "",
        )}
        style={fullHeight || height === "100%" ? { height: "100%" } : undefined}
      >
        <div className="absolute right-4 top-1 z-10 flex items-center">
          {editorOptions?.readOnly && (
            <CopyButton
              text={code?.trim() || ""}
              onCopy={() => onCopyCode?.(code)}
            />
          )}
          {showFullScreenButton && (
            <Button
              variant="ghost"
              size="sm"
              type="button"
              onClick={() => setIsFullscreen(true)}
            >
              <ArrowsExpandIcon className="h-4 w-4" />
            </Button>
          )}
        </div>
        <Editor
          height={fullHeight || height === "100%" ? "100%" : height}
          width="100%"
          defaultLanguage={monacoLanguage}
          language={monacoLanguage}
          value={code?.trim()}
          onChange={(value) => handleCodeChange?.(value?.trim() || "")}
          options={{ ...editorOptions, ...EDITOR_OPTIONS }}
          className="w-full"
        />
      </div>
      {dataIsValid && !dataIsValid.code && (
        <div className="col-span-3 mt-2 text-left text-xs text-red-500">
          Code is required
        </div>
      )}
      {showRuntimeDetails && (
        <div className="col-span-3 text-sm text-gray-500">
          <RuntimeDetails language={selectedLanguage?.toUpperCase()} />
        </div>
      )}
      <Dialog open={isFullscreen} onOpenChange={setIsFullscreen}>
        <DialogContent className="max-w-[90vw]">
          <div ref={modalContainerRef} className="h-[80vh]">
            <div className="absolute right-12 top-10 z-10 flex items-center">
              {editorOptions?.readOnly && (
                <CopyButton
                  text={code?.trim() || ""}
                  onCopy={() => onCopyCode?.(code)}
                />
              )}
            </div>
            <Editor
              defaultLanguage={monacoLanguage}
              language={monacoLanguage}
              value={code?.trim()}
              onChange={(value) => handleCodeChange?.(value?.trim() || "")}
              options={{ ...editorOptions, ...EDITOR_OPTIONS }}
              className="mt-4 h-full w-full"
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CodeEditor;
